import React, { useState } from "react";
import { ArrowRight, Bell, BookOpen, Compass, GraduationCap, Hourglass } from "lucide-react";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLocalization } from "@/lib/localization";
import { useTutorial } from "@/features/tutorial/TutorialContext";
import { getTutorialPageCopy, lessonIcons } from "@/features/tutorial/tutorialCopy";

export default function Tutorials() {
  const { language } = useLocalization();
  const tutorial = useTutorial();
  const copy = getTutorialPageCopy(language);
  const [selected, setSelected] = useState(null);

  return (
    <PageContainer>
      <section className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#064e4f] px-6 py-7 text-white shadow-[0_30px_80px_-45px_rgba(6,78,79,0.85)] sm:px-8 sm:py-9">
        <div className="pointer-events-none absolute -right-16 -top-24 h-64 w-64 rounded-full bg-cyan-300/10 blur-3xl" />
        <div className="relative grid gap-8 lg:grid-cols-[1fr_auto] lg:items-end">
          <div>
            <div className="flex items-center justify-between">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/15"><GraduationCap className="h-5 w-5" /></span>
              <span className="rounded-full bg-white/10 px-3 py-1 text-[10px] font-semibold tracking-[0.18em] text-white/70 ring-1 ring-white/15">10 GUIDES</span>
            </div>
            <p className="mt-7 text-[10px] font-semibold uppercase tracking-[0.25em] text-white/55">{copy.eyebrow}</p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight">{copy.title}</h1>
            <p className="mt-3 max-w-xl text-sm leading-6 text-white/65">{copy.intro}</p>
          </div>
          <Button disabled={!tutorial.eligible} onClick={tutorial.restart} title={!tutorial.eligible ? "Sell, Inventory, History and Dashboard access are required." : undefined} className="h-11 gap-2 bg-white px-5 text-[#064e4f] hover:bg-white/90 disabled:bg-white/55">
            <Compass className="h-4 w-4" /> {copy.start}
          </Button>
        </div>
      </section>

      <section className="mt-6 overflow-hidden rounded-2xl border border-border/70 bg-card shadow-sm">
        <div className="grid md:grid-cols-2">
          {copy.names.map((name, index) => {
            const Icon = lessonIcons[index] || BookOpen;
            return (
              <button
                key={name}
                type="button"
                onClick={() => setSelected(index)}
                className={`group flex min-h-[82px] items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-muted/45 ${index > 0 ? "border-t border-border/60" : ""} ${index === 1 ? "md:border-t-0" : ""} ${index % 2 === 1 ? "md:border-l" : ""}`}
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/8 text-primary"><Icon className="h-[18px] w-[18px]" /></span>
                <span className="min-w-0 flex-1 text-sm font-medium text-foreground">{name}</span>
                <span className="font-mono text-[10px] tracking-wider text-muted-foreground/65">{String(index + 1).padStart(2, "0")}</span>
                <ArrowRight className="h-4 w-4 text-muted-foreground/45 transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
              </button>
            );
          })}
        </div>
      </section>

      <Dialog open={selected !== null} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-md overflow-hidden border-primary/10 p-0 shadow-2xl">
          <div className="bg-gradient-to-br from-[#064e4f] to-[#08716d] px-6 py-7 text-white">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/15"><Hourglass className="h-5 w-5" /></span>
            <DialogHeader className="mt-5 text-left">
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-white/60">{copy.coming}</p>
              <DialogTitle className="mt-2 text-xl text-white">{copy.comingTitle}</DialogTitle>
              <DialogDescription className="mt-2 leading-6 text-white/70">{selected === null ? "" : copy.comingBody(copy.names[selected])}</DialogDescription>
            </DialogHeader>
          </div>
          <div className="px-6 py-5">
            <div className="flex items-start gap-3 rounded-xl border bg-muted/35 p-4 text-sm leading-5 text-muted-foreground"><Bell className="mt-0.5 h-4 w-4 shrink-0 text-primary" />{copy.comingNote}</div>
          </div>
          <DialogFooter className="border-t bg-muted/15 px-6 py-5"><Button onClick={() => setSelected(null)} className="w-full">{copy.close}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
